import type { StaffMemberInput } from '@components/settings/dashboard/summaries';
import { supabase } from './supabase';

/**
 * Data reads behind the Settings Dashboard rows. Each helper throws on
 * failure so its TanStack Query surfaces a per-row error state instead of
 * silently rendering zeros.
 */

/**
 * Role, status and passcode coverage for an outlet's staff (or the whole
 * company when outletId is null). company_users_view already exposes
 * has_passcode, so this works for managers who can't read user_passcodes.
 */
export async function fetchStaffSnapshot(
  outletId: string | null,
): Promise<StaffMemberInput[]> {
  let query = supabase
    .from('company_users_view')
    .select('role, is_active, has_passcode');
  if (outletId) query = query.eq('outlet_id', outletId);

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return ((data ?? []) as StaffMemberInput[]).map((row) => ({
    role: row.role,
    is_active: Boolean(row.is_active),
    has_passcode: Boolean(row.has_passcode),
  }));
}

export interface EbmSubmissionCounts {
  failed: number;
  pending: number;
}

/**
 * Failed vs still-unconfirmed EBM sale submissions. Head-only count
 * queries, so no rows are transferred.
 */
export async function fetchEbmSubmissionCounts(
  outletId: string | null,
): Promise<EbmSubmissionCounts> {
  const countByStatus = async (statuses: string[]) => {
    let query = supabase
      .from('ebm_sale_records_view')
      .select('id', { count: 'exact', head: true })
      .in('submission_status', statuses);
    if (outletId) query = query.eq('outlet_id', outletId);
    const { count, error } = await query;
    if (error) throw new Error(error.message);
    return count ?? 0;
  };

  const [failed, pending] = await Promise.all([
    countByStatus(['FAILED']),
    countByStatus(['PENDING', 'SUBMITTED_QUEUED', 'OVERRIDDEN_UNCONFIRMED']),
  ]);
  return { failed, pending };
}

export type EbmActivationStatus =
  | 'NOT_CONFIGURED'
  | 'PENDING_INIT'
  | 'ACTIVE'
  | 'FAILED';

export interface OutletFleetStatus {
  ebmStatus: EbmActivationStatus | null;
  ebmHealthOk: boolean | null;
  activePrinters: number;
}

/**
 * Per-outlet EBM and printer posture for the company strip. Reads Supabase
 * rather than local SQLite because only the device's own outlet is synced.
 */
export async function fetchOutletFleetStatus(
  outletIds: string[],
): Promise<Record<string, OutletFleetStatus>> {
  if (outletIds.length === 0) return {};

  const [ebmResult, printersResult] = await Promise.all([
    supabase
      .from('outlet_ebm_configs')
      .select('outlet_id, activation_status, last_health_ok')
      .in('outlet_id', outletIds),
    supabase
      .from('outlet_printers')
      .select('outlet_id')
      .in('outlet_id', outletIds)
      .eq('is_active', true),
  ]);
  if (ebmResult.error) throw new Error(ebmResult.error.message);
  if (printersResult.error) throw new Error(printersResult.error.message);

  const status: Record<string, OutletFleetStatus> = {};
  outletIds.forEach((id) => {
    status[id] = { ebmStatus: null, ebmHealthOk: null, activePrinters: 0 };
  });
  (
    (ebmResult.data ?? []) as {
      outlet_id: string;
      activation_status: EbmActivationStatus;
      last_health_ok: boolean | null;
    }[]
  ).forEach((row) => {
    if (!status[row.outlet_id]) return;
    status[row.outlet_id].ebmStatus = row.activation_status;
    status[row.outlet_id].ebmHealthOk = row.last_health_ok;
  });
  ((printersResult.data ?? []) as { outlet_id: string }[]).forEach((row) => {
    if (status[row.outlet_id]) status[row.outlet_id].activePrinters += 1;
  });
  return status;
}
